import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trash2, Copy, Send, KeyRound } from "lucide-react";
import NewUserForm from "@/components/NewUserForm";

const empty = { name: "", email: "", role: "standard", password: "" };

function InviteRow({ invite: i, onResend, onRevoke }) {
  return (
    <div data-testid={`invite-row-${i.id}`} className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0 text-sm">
      <div className="min-w-0">
        <div className="font-medium truncate">{i.name} <span className="text-muted-foreground">· {i.email}</span></div>
        <div className="text-xs text-muted-foreground capitalize">{i.role} · {i.expired ? <span className="text-destructive">expired</span> : `expires ${i.expires_at.slice(0, 10)}`}</div>
      </div>
      <div className="flex gap-1 shrink-0">
        <Button size="sm" variant="outline" onClick={() => onResend(i.id)} data-testid={`invite-resend-${i.id}`}><Send className="w-3.5 h-3.5 mr-1" /> Resend</Button>
        <Button size="sm" variant="ghost" onClick={() => onRevoke(i.id)} data-testid={`invite-revoke-${i.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
      </div>
    </div>
  );
}

function useInviteActions(onChanged) {
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState("");
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); onChanged(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };
  const invite = (form) => run(async () => {
    const { data } = await api.post("/admin/invitations", { name: form.name, email: form.email, role: form.role });
    toast.success(`Invitation emailed to ${data.email}`);
    setLastLink(data.invite_link);
  });
  const addDirect = (form) => run(async () => {
    const { data } = await api.post("/admin/users", form);
    toast.success(`${data.name} added — they can sign in now with the password you set`);
    setLastLink("");
  });
  const resend = (id) => run(async () => {
    const { data } = await api.post(`/admin/invitations/${id}/resend`);
    toast.success(`Invitation re-sent to ${data.email}`);
    setLastLink(data.invite_link);
  });
  const revoke = (id) => run(async () => { await api.delete(`/admin/invitations/${id}`); toast.success("Invitation revoked"); });
  return { busy, lastLink, invite, addDirect, resend, revoke };
}

export default function InvitePanel({ invites, onChanged }) {
  const [mode, setMode] = useState("invite");
  const [form, setForm] = useState(empty);
  const { busy, lastLink, invite, addDirect, resend, revoke } = useInviteActions(onChanged);

  const submit = async (e) => {
    e.preventDefault();
    await (mode === "invite" ? invite(form) : addDirect(form));
    setForm(empty);
  };
  const copy = async () => { await navigator.clipboard.writeText(lastLink); toast.success("Invite link copied"); };

  return (
    <Card className="card-shadow-soft" data-testid="invite-panel">
      <CardHeader>
        <CardTitle className="text-lg">Add a new user</CardTitle>
        <Tabs value={mode} onValueChange={setMode} className="mt-2">
          <TabsList>
            <TabsTrigger value="invite" data-testid="mode-invite"><Send className="w-3.5 h-3.5 mr-1.5" /> Email an invite</TabsTrigger>
            <TabsTrigger value="manual" data-testid="mode-manual"><KeyRound className="w-3.5 h-3.5 mr-1.5" /> Add with a password</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent className="space-y-4">
        <NewUserForm mode={mode} form={form} setForm={setForm} busy={busy} onSubmit={submit} />
        {lastLink && (
          <div className="flex items-center gap-2 text-xs bg-secondary rounded-md p-2" data-testid="invite-link-box">
            <span className="truncate flex-1 font-mono">{lastLink}</span>
            <Button size="sm" variant="outline" onClick={copy} data-testid="invite-copy"><Copy className="w-3.5 h-3.5 mr-1" /> Copy</Button>
          </div>
        )}
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Outstanding invitations ({invites.length})</div>
          {invites.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-invites">None outstanding.</p>}
          {invites.map((i) => <InviteRow key={i.id} invite={i} onResend={resend} onRevoke={revoke} />)}
        </div>
      </CardContent>
    </Card>
  );
}
